import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { db } from '../../firebase/config';
import { collection, query, orderBy, onSnapshot } from 'firebase/firestore';
import { getSiniestroStatusChip, getPersonaChipClass } from '../../utils/styles';

const IconAlert = () => (
    <svg className="w-4 h-4 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
    </svg>
);

const IconHistory = () => (
    <svg className="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
);

const formatDate = (dateValue) => {
    if (!dateValue) return '-';
    if (dateValue.seconds) {
        return new Date(dateValue.seconds * 1000).toLocaleDateString('es-AR', {
            day: '2-digit', month: '2-digit', year: 'numeric'
        });
    }
    if (dateValue instanceof Date) return dateValue.toLocaleDateString('es-AR');
    if (dateValue.toDate) return dateValue.toDate().toLocaleDateString('es-AR');
    if (typeof dateValue === 'string' && dateValue.includes('-')) {
        const parts = dateValue.split('-');
        return parts.length === 3 ? `${parts[2]}/${parts[1]}/${parts[0]}` : dateValue;
    }
    return dateValue;
};

const ModalDetalleSiniestrosPolicy = ({ show, onClose, poliza, client, siniestros = [] }) => {
    const [expandedSiniestroId, setExpandedSiniestroId] = useState(null);
    const [gestionesHistorial, setGestionesHistorial] = useState({});

    // Sincronización en tiempo real de gestiones para el siniestro expandido
    useEffect(() => {
        if (!expandedSiniestroId || !poliza?.id || !client?.id) return;

        const path = `public/data/clientes/${client.id}/polizas/${poliza.id}/siniestros/${expandedSiniestroId}/gestiones`;
        const gestionesRef = collection(db, path);
        const q = query(gestionesRef, orderBy('fechaCreacion', 'desc'));

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setGestionesHistorial(prev => ({
                ...prev,
                [expandedSiniestroId]: data
            }));
        }, (error) => {
            console.error("Error cargando gestiones para modal:", error);
        });

        return () => unsubscribe();
    }, [expandedSiniestroId, poliza?.id, client?.id]);

    // Al abrir el modal, inicia con el primer siniestro abierto opcionalmente
    useEffect(() => {
        if (show && siniestros.length > 0) {
            setExpandedSiniestroId(siniestros[0].id);
        } else if (!show) {
            setExpandedSiniestroId(null);
        }
    }, [show]);

    if (!show) return null;

    // Métricas para el resumen superior
    const totalCount = siniestros.length;
    const abiertosCount = siniestros.filter(s => (s.estadoGestion || '').toLowerCase().includes('abierto')).length;
    const enTramiteCount = siniestros.filter(s => (s.estadoGestion || '').toLowerCase().includes('trámite') || (s.estadoGestion || '').toLowerCase().includes('pendiente')).length;
    const cerradosCount = siniestros.filter(s => (s.estadoGestion || '').toLowerCase().includes('cerrado') || (s.estadoGestion || '').toLowerCase().includes('liquidado') || (s.estadoGestion || '').toLowerCase().includes('rechazado')).length;

    const toggleExpand = (id) => {
        setExpandedSiniestroId(prev => (prev === id ? null : id));
    };

    return createPortal(
        <div className="fixed inset-0 bg-slate-900/60 z-[1200] flex items-center justify-center p-4 backdrop-blur-sm transition-opacity text-left font-sans">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl h-auto max-h-[92vh] flex flex-col overflow-hidden border border-slate-200 animate-fade-in transition-all duration-300">
                
                {/* HEADER */}
                <div className="flex justify-between items-center px-6 py-4 border-b border-slate-100 bg-red-50/40 shrink-0">
                    <div className="flex items-center gap-3">
                        <span className="bg-red-100 text-red-600 p-2.5 rounded-xl shrink-0 shadow-sm">
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                            </svg>
                        </span>
                        <div>
                            <div className="flex items-center gap-2">
                                <h3 className="text-lg font-bold text-slate-800 leading-none uppercase">
                                    Expediente de Siniestros
                                </h3>
                                <span className="bg-red-600 text-white text-[10px] font-black px-2.5 py-0.5 rounded-full uppercase tracking-wider">
                                    Póliza N° {poliza?.nPoliza || poliza?.numeroPoliza || '-'}
                                </span>
                            </div>
                            <p className="text-[11px] text-slate-500 font-semibold mt-1">
                                Cliente: <strong className="text-slate-800 uppercase">{client?.nombre || client?.displayNombre} {client?.apellido || ''}</strong> • Cía: <strong className="text-slate-800 uppercase">{poliza?.compania || '-'}</strong> ({poliza?.riesgo || '-'})
                            </p>
                        </div>
                    </div>
                    <button onClick={onClose} className="text-slate-400 hover:text-slate-600 transition-colors p-2 hover:bg-slate-100 rounded-full cursor-pointer">
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                {/* BARRA DE MÉTRICAS */}
                <div className="bg-slate-50 px-6 py-3 border-b border-slate-200/80 flex items-center justify-between gap-3 shrink-0">
                    <div className="flex items-center gap-2">
                        <div className="bg-white border border-slate-200 px-3 py-1.5 rounded-xl shadow-xs text-xs font-bold text-slate-700 flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full bg-slate-800"></span>
                            <span>Total: <strong className="font-mono text-slate-900">{totalCount}</strong></span>
                        </div>
                        <div className="bg-white border border-red-200 px-3 py-1.5 rounded-xl shadow-xs text-xs font-bold text-red-700 flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full bg-red-600 animate-pulse"></span>
                            <span>Abiertos: <strong className="font-mono">{abiertosCount}</strong></span>
                        </div>
                        <div className="bg-white border border-amber-200 px-3 py-1.5 rounded-xl shadow-xs text-xs font-bold text-amber-700 flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full bg-amber-500"></span>
                            <span>En Trámite: <strong className="font-mono">{enTramiteCount}</strong></span>
                        </div>
                        <div className="bg-white border border-emerald-200 px-3 py-1.5 rounded-xl shadow-xs text-xs font-bold text-emerald-700 flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full bg-emerald-500"></span>
                            <span>Cerrados: <strong className="font-mono">{cerradosCount}</strong></span>
                        </div>
                    </div>
                </div>

                {/* CUERPO DEL MODAL - LISTADO DE SINIESTROS */}
                <div className="overflow-y-auto p-6 flex-grow bg-slate-50/50 space-y-4 custom-scrollbar">
                    {siniestros.length === 0 ? (
                        <div className="text-center py-16 bg-white rounded-2xl border border-slate-200/80 p-8 space-y-2">
                            <span className="text-3xl block">📋</span>
                            <p className="text-xs font-bold text-slate-400 uppercase tracking-wider">
                                No hay siniestros registrados para esta póliza.
                            </p>
                        </div>
                    ) : (
                        siniestros.map((siniestro, index) => {
                            const isExpanded = expandedSiniestroId === siniestro.id;
                            const gestiones = gestionesHistorial[siniestro.id] || [];

                            return (
                                <div key={siniestro.id || index} className="bg-white rounded-2xl border border-slate-200 shadow-xs overflow-hidden transition-all duration-200">
                                    
                                    {/* CABECERA TARJETA SINIESTRO */}
                                    <div 
                                        onClick={() => toggleExpand(siniestro.id)}
                                        className={`p-4 flex flex-wrap items-center justify-between gap-4 cursor-pointer select-none transition-colors ${
                                            isExpanded ? 'bg-red-50/20 border-b border-slate-100' : 'hover:bg-slate-50'
                                        }`}
                                    >
                                        <div className="flex items-center gap-3">
                                            <div className={`p-1.5 rounded-lg border transition-transform duration-300 ${isExpanded ? 'rotate-90 bg-red-100 border-red-200 text-red-600' : 'bg-slate-100 border-slate-200 text-slate-400'}`}>
                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="3">
                                                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                                                </svg>
                                            </div>

                                            <div>
                                                <div className="flex items-center gap-2">
                                                    <span className="text-sm font-black text-slate-900 uppercase">
                                                        {siniestro.tipoSiniestro || 'Siniestro Denunciado'}
                                                    </span>
                                                    {siniestro.numeroSiniestro && (
                                                        <span className="font-mono font-bold text-xs bg-slate-100 text-slate-700 px-2 py-0.5 rounded border border-slate-200">
                                                            N° {siniestro.numeroSiniestro}
                                                        </span>
                                                    )}
                                                </div>
                                                <div className="flex items-center gap-3 text-[11px] text-slate-500 font-semibold mt-1">
                                                    <span>F. Siniestro: <strong className="text-slate-800 font-mono">{formatDate(siniestro.fechaSiniestro)}</strong></span>
                                                    <span>•</span>
                                                    <span>F. Carga: <strong className="text-slate-800 font-mono">{formatDate(siniestro.fechaCarga)}</strong></span>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="flex items-center gap-3">
                                            {siniestro.quienGestiono && (
                                                <div className={`px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wide ${getPersonaChipClass(siniestro.quienGestiono)}`}>
                                                    {siniestro.quienGestiono}
                                                </div>
                                            )}

                                            <span className={`px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-wide border ${getSiniestroStatusChip(siniestro.estadoGestion)}`}>
                                                {siniestro.estadoGestion || 'Abierto'}
                                            </span>
                                        </div>
                                    </div>

                                    {/* CONTENIDO EXPANDIBLE */}
                                    {isExpanded && (
                                        <div className="p-6 bg-slate-50/60 space-y-6 animate-slide-down border-t border-slate-100">
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                                
                                                {/* DETALLE DEL SINIESTRO */}
                                                <div className="space-y-2">
                                                    <h4 className="font-bold text-slate-400 uppercase text-[10px] tracking-widest flex items-center gap-2">
                                                        <IconAlert /> Detalle de los Hechos & Daños
                                                    </h4>
                                                    <div className="pl-3 border-l-4 border-red-300">
                                                        <div className="bg-white p-4 rounded-xl border border-slate-200/80 shadow-xs text-slate-700 text-xs leading-relaxed whitespace-pre-wrap italic min-h-[90px]">
                                                            {siniestro.detalleSiniestro 
                                                                ? `"${siniestro.detalleSiniestro}"` 
                                                                : 'Sin detalle de los hechos especificado.'}
                                                        </div>
                                                    </div>
                                                    
                                                    {siniestro.cantidadRuedas && (
                                                        <div className="mt-2 inline-flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 px-3 py-1 rounded-lg text-xs font-bold">
                                                            <span>🛞 Ruedas afectadas:</span>
                                                            <strong className="font-mono font-black">{siniestro.cantidadRuedas}</strong>
                                                        </div>
                                                    )}
                                                </div>

                                                {/* HISTORIAL Y BITÁCORA DE GESTIONES */}
                                                <div className="space-y-2">
                                                    <h4 className="font-bold text-slate-400 uppercase text-[10px] tracking-widest flex items-center gap-2">
                                                        <IconHistory /> Historial de Movimientos ({gestiones.length})
                                                    </h4>
                                                    <div className="pl-3 border-l-4 border-blue-300 space-y-2 max-h-[220px] overflow-y-auto pr-2 custom-scrollbar">
                                                        {gestiones.length === 0 ? (
                                                            <p className="text-slate-400 italic text-xs bg-white p-3 rounded-xl border border-slate-200/80">
                                                                No hay movimientos registrados en el seguimiento.
                                                            </p>
                                                        ) : (
                                                            gestiones.map((g, idx) => (
                                                                <div key={g.id || idx} className="bg-white p-3 rounded-xl border border-slate-200/80 shadow-xs text-slate-700 text-xs space-y-1">
                                                                    <div className="flex items-center justify-between">
                                                                        <span className="text-blue-800 font-black uppercase text-[10px] tracking-wide bg-blue-50 px-2 py-0.5 rounded border border-blue-100">
                                                                            {g.tipo || g.tipoGestion || 'Nota'}
                                                                        </span>
                                                                        <span className="text-[10px] font-bold text-slate-400 tabular-nums">
                                                                            {formatDate(g.fechaCreacion)}
                                                                        </span>
                                                                    </div>
                                                                    <p className="text-slate-600 font-medium leading-relaxed italic pt-1">
                                                                        {g.observacion}
                                                                    </p>
                                                                </div>
                                                            ))
                                                        )}
                                                    </div>
                                                </div>

                                            </div>
                                        </div>
                                    )}

                                </div>
                            );
                        })
                    )}
                </div>

                {/* BOTONERA INFERIOR */}
                <div className="px-6 py-3.5 border-t border-slate-200/80 flex justify-between items-center bg-white shrink-0">
                    <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">
                        Expediente Unificado de Siniestros
                    </span>
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-6 py-2 rounded-full border border-slate-300 bg-white text-slate-700 font-bold uppercase text-xs hover:bg-slate-50 shadow-xs transition-all transform active:scale-95 cursor-pointer"
                    >
                        Cerrar
                    </button>
                </div>

            </div>
        </div>,
        document.body
    );
};

export default ModalDetalleSiniestrosPolicy;